import { Component, OnInit, Input, SimpleChanges, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { ValueChangeEvent } from './../forms/input-control.base';
import { AbstractControl, FormGroup, FormBuilder } from '@angular/forms';
import { LocalDate } from '../../model/local-date';
import { FormControlExFactory } from '../../service/factory/form-control-ex-factory.service';
import { FormControlEx } from '../forms/form-control';
import { Subscription } from 'rxjs';

/**
 * @astral-compatible Retained for legacy LocalDate callback behavior.
 * Prefer `astral-date-picker` for new single-control and grouped-date forms.
 */
@Component({
    selector: 'app-date-picker',
    templateUrl: './date-picker.component.html',
    styleUrls: ['./date-picker.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DatePickerComponent implements OnInit {
  constructor(
    private _controlModelFactory: FormControlExFactory,
    private fb: FormBuilder
  ) {
    if (!(this.readonly === true)) this.readonly = false;
  }
  // Accept AbstractControl so templates can bind `form.controls['x']`; used
  // internally as a FormGroup (addControl/get/disable/...), so expose as one.
  private _form: FormGroup;
  @Input() set form(value: AbstractControl) {
    this._form = value as FormGroup;
  }
  get form(): FormGroup {
    return this._form;
  }
  @Input() helpText: string;
  @Input() isRequired: boolean;
  @Input() model: LocalDate;
  @Input() title: string;
  @Input() readonly: boolean;
  @Input() showEmptyReadOnly: boolean;
  @Input() clearOthers: boolean;
  @Output() valueChange: EventEmitter<LocalDate> = new EventEmitter();
  @Output() valueChangeFull: EventEmitter<ValueChangeEvent> = new EventEmitter();
  initialized: boolean;
  private _touchedCheckInterval: any;
  private _dateControlSubscription: Subscription;
  private _updatingDateControl = false;

  private _dateControl: FormControlEx;
  get dateControl(): FormControlEx {
    if (!this._dateControl)
      this._dateControl = this._controlModelFactory.createWithOptions('DATE', this.date, {
        required: this.isRequired,
        isReadonly: this.readonly,
      });
    return this._dateControl;
  }

  display: boolean = true;

  private _internalForm: FormGroup;
  get internalForm(): FormGroup {
    if (!this._internalForm) this._internalForm = this.fb.group({ date: this.dateControl });
    return this._internalForm;
  }

  get date() {
    let localDate = this.model;
    if (localDate === null || localDate === undefined) return null;
    if (!(localDate instanceof LocalDate)) this.model = localDate = LocalDate.fromDate(this.model);
    return localDate.toDate();
  }

  set date(date) {
    let localDate = LocalDate.fromDate(date);
    this.dateControl.setValue(localDate.hasValue() ? localDate.toDate() : '');

    if (this.model === null || this.model === undefined) this.model = localDate;
    else this.model.setDate(localDate.toDate());
  }

  ngOnChanges(changes: SimpleChanges): void {
    const controlModelNames = ['year', 'month', 'day'];

    if (changes['isRequired'] && this.initialized) {
      var isRequired = changes['isRequired'].currentValue;

      if (this.form) controlModelNames.forEach(name => ((this.form.controls[name] as FormControlEx).required = isRequired));

      (this.dateControl as FormControlEx).required = isRequired;
    }

    if (changes['readonly'] && this.initialized) {
      let isReadOnly = changes['readonly'].currentValue;
      if (isReadOnly) {
        if (this.form) {
          controlModelNames.forEach(name => ((this.form.controls[name] as FormControlEx).isReadonly = true));
          this.form.disable();
        }
        this.internalForm.disable();
      } else {
        if (this.form) {
          controlModelNames.forEach(name => ((this.form.controls[name] as FormControlEx).isReadonly = false));
          this.form.enable();
        }
        this.internalForm.enable();
      }
    }

    if (this.showEmptyReadOnly) this.display = true;
    else this.display = (this.model !== null && this.model !== undefined) || !this.readonly;
  }

  ngOnInit() {
    if (this.model === null || this.model === undefined) this.model = LocalDate.fromDate(null);

    if (this.form) {
      if (!this.form.get('year'))
        this.form.addControl(
          'year',
          this._controlModelFactory.createWithOptions('COMMON.YEAR', this.model.year, { required: this.isRequired })
        );
      if (!this.form.get('month'))
        this.form.addControl(
          'month',
          this._controlModelFactory.createWithOptions('COMMON.MONTH', this.model.month, { required: this.isRequired })
        );
      if (!this.form.get('day'))
        this.form.addControl(
          'day',
          this._controlModelFactory.createWithOptions('COMMON.DAY', this.model.day, { required: this.isRequired })
        );
      if (this.readonly) this.form.disable();
      else this.form.enable();

      this.form.valueChanges.subscribe(value => this._onFormChanged(value));
      this._synchronizeFormTouchedEvent();
    }

    this._dateControlSubscription = this.dateControl.valueChanges.subscribe(value => {
      if (this._updatingDateControl || !this.initialized) {
        return;
      }

      this._applyDateChange(LocalDate.fromDate(value).toDate());
    });

    this.initialized = true;
  }

  private _synchronizeFormTouchedEvent(): void {
    if (!this.form) return;

    this._touchedCheckInterval = setInterval(() => {
      if (this.form.touched && !this._dateControl.touched) {
        this._dateControl.markAsTouched();
        this._dateControl.updateValueAndValidity();
      }
    }, 300);
  }

  private _onFormChanged(value) {
    if (this._selfUpdating) return;
    let year = this.form.controls['year'].value;
    let month = this.form.controls['month'].value;
    let day = this.form.controls['day'].value;
    if (year && month && day) {
      let date = new LocalDate(year, month, day).toDate();
      if (date !== this.date) {
        if (this.model === null || this.model === undefined) this.model = new LocalDate(null, null, null);
        this.model.setDate(date);
        this.dateControl.setValue(date);
        this.internalForm.markAsDirty();
        this.valueChange.emit(this.model);
      }
    }
  }

  private _selfUpdating: boolean;
  onKeyUp(a: any) {
    let value = a.currentTarget.value;
    if (typeof value === 'string') {
      if (/^\d{8}$/.test(value)) {
        let year = parseFloat(value.substr(0, 4));
        let month = parseFloat(value.substr(4, 2));
        let day = parseFloat(value.substr(6, 2));
        let date = new Date(year, month - 1, day);
        if (!isNaN(date.getTime())) {
          this.internalForm.controls['date'].setValue(date);
        }
      }
    }
  }
  onDateChange(date: Date) {
    this._applyDateChange(date);
  }

  get errorMessage(): { message: string; params: any } {
    if (!this.dateControl || !this.dateControl.invalid || !this.dateControl.errors) {
      return null;
    }

    let key = Object.keys(this.dateControl.errors)[0];
    switch (key) {
      case 'matDatepickerParse':
        return { message: 'ERROR.DATE', params: {} };

      default:
        return this.dateControl.getErrorMessage();
    }
  }

  get showClearOthers(): boolean {
    if (!this.clearOthers) {
      return false;
    }
    // return (Object.keys(this.control.parent.controls).length > 1
    // && (this.control.value || this.control.value === 0));
  }

  addonClicked(): boolean {
    // for (let controlName of Object.keys(this.control.parent.controls)) {
    // let currentControl = this.control.parent.get(controlName);
    // if (currentControl !== this.control) {
    // currentControl.setValue(undefined);
    // }
    // }
    // this.addonClick.emit(this.control);
    return false;
  }

  clearBtnClicked() {
    // this.onClearBtnMouseLeave();
    // this.model = LocalDate.fromDate(null);
    // this.form.patchValue(this.model);
    // this.onDateChange(this.form.value);
    // this._onFormChanged(this.model);
    // for (let controlName of Object.keys(this.control.parent.controls)) {
    // let currentControl = this.control.parent.get(controlName);
    // if (currentControl === this.control) {
    // currentControl.setValue(undefined);
    // }
    // }
    // this.addonClick.emit(this.control);
    // return false;
  }

  ngOnDestroy(): void {
    if (this._touchedCheckInterval) {
      clearInterval(this._touchedCheckInterval);
    }

    this._dateControlSubscription?.unsubscribe();
  }

  private _applyDateChange(date: Date | null): void {
    if (date === null && this.date === null) return;

    const originalValue = LocalDate.fromDate(this.date);

    try {
      this._selfUpdating = true;
      this._updatingDateControl = true;
      this.form?.markAsDirty();
      this.internalForm.markAsDirty();

      this.date = date;
      if (this.form) {
        if (this.model === null || this.model === undefined) {
          this.form.setValue({
            year: null,
            month: null,
            day: null,
          });
        } else {
          this.form.setValue({
            year: this.model.year,
            month: this.model.month,
            day: this.model.day,
          });
        }
      }
    } finally {
      this._updatingDateControl = false;
      this._selfUpdating = false;
    }

    this.valueChange.emit(this.model);

    if (this.form?.parent) {
      this.valueChangeFull.emit({
        formSnapshot: Object.assign({}, this.form.parent.value),
        newValue: this.model,
        previousValue: originalValue,
      });
    }
  }
}
